import * as S from "./styled"
import { TTicketDisposal } from "../../utils/@types/data/ticket"
import { formatMoney } from "../../utils/tb/formatMoney"
import { useNavigate } from "react-router-dom"
import getStore from "../../store"
import { memo, useCallback, useEffect, useState, Dispatch, SetStateAction } from "react"
import ReactDOM from "react-dom"
import { eventHasTaxes, sumTaxes } from "../../utils/tb/taxes"
import TicketsGroup from "../TicketsGroup"
import Ticket from "../Ticket"
import MesaSectorGroup from "../MesaSectorGroup"
import { checkEndingDate } from "../../utils/tb/checkEndingDate"

type Props = {
  showByGroup: boolean
  groups: { id: string; name: string }[]
  tickets: TTicketDisposal[]
  setTickets: Dispatch<SetStateAction<TTicketDisposal[]>>
  inline?: boolean
}

const TicketsControl = ({
  showByGroup,
  tickets,
  setTickets,
  groups,
  inline = false,
}: Props) => {
  const navigate = useNavigate()

  const { event } = getStore()

  const [taxes, setTaxes] = useState({
    value: 0,
    rule: "",
    strComplement: "",
  })
  const [ticketsTotal, setTicketsTotal] = useState(0)
  const [total, setTotal] = useState(0)

  // Mesa state
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [selectedMesas, setSelectedMesas] = useState<Record<string, string>>({})
  const [expandedSector, setExpandedSector] = useState<Record<string, string | null>>({})
  const [mapModal, setMapModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  })

  const calcTotal = useCallback(() => {
    let val = tickets.reduce((qnt, tk) => qnt + tk.price_sell * tk.qnt, 0)

    return val
  }, [tickets])

  const calcTotals = useCallback(() => {
    if (event) {
      const tTotal = calcTotal()

      const tax = sumTaxes({
        adminTax: event?.eCommerce?.adminTax,
        adminTaxMinimum: +event?.eCommerce?.adminTaxMinimum,
        adminTaxPercentage: +event?.eCommerce?.adminTaxPercentage,
        adminTaxValue: +event?.eCommerce?.adminTaxValue,
        tickets: tickets,
      })

      setTaxes(tax)
      setTicketsTotal(tTotal)

      const clientCharges = event?.eCommerce?.chargeClient
        ? tTotal + tax.value
        : tTotal

      setTotal(tTotal > 0 ? clientCharges : 0)
    }
  }, [calcTotal, event, tickets])

  const changeQnt = (
    ticketId: number | string,
    action: "decrease" | "increase",
  ) => {
    let nList = [...tickets]

    const tk = nList.find((t) => t.id === ticketId) as TTicketDisposal

    if (action === "decrease") {
      if (tk.qnt > 0) {
        nList = nList.map((tkt) =>
          tkt.id !== tk.id
            ? tkt
            : {
                ...tkt,
                qnt: tk.qnt - 1,
              },
        )
      }
    } else if (action === "increase") {
      if (tk.qnt + 1 <= tk.quantity && tk.qnt + 1 <= 20) {
        nList = nList.map((tkt) =>
          tkt.id !== tk.id
            ? tkt
            : {
                ...tkt,
                qnt: tk.qnt + 1,
              },
        )
      }
    }

    setTickets(nList)

    calcTotals()
  }

  const handleBuy = () => {
    navigate("/payment", {
      state: { tickets },
    })
    window.scrollTo({ top: 0 })
  }

  useEffect(() => {
    calcTotals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets])

  // Initialize activeTab when mesa tickets are present
  useEffect(() => {
    const mesaTickets = tickets.filter((t) => t.mesa_type !== null)
    const types = [
      ...new Set(
        mesaTickets
          .map((t) => t.mesa_type)
          .filter((v): v is "mesa" | "camarote" => v !== null),
      ),
    ]
    if (types.length > 0 && !activeTab) {
      setActiveTab(types[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets])

  const renderKeepOutSellsMessage = () => {
    let message = ""

    if (event) {
      const isEventStillAvailable = checkEndingDate(event as any)

      if (isEventStillAvailable) {
        if (!Boolean(event?.keep_sells_online)) {
          message =
            event?.keep_out_sells_online_message ?? "Vendas online suspensas."
        }
      } else message = "Vendas online encerradas"
    }

    return message !== "" ? (
      <S.KeepOutSellsOnlineMessage>{message}</S.KeepOutSellsOnlineMessage>
    ) : null
  }

  const mesaTickets = tickets.filter((t) => t.mesa_type !== null)
  const regularTickets = tickets.filter((t) => t.mesa_type === null)

  const mesaTypes = [
    ...new Set(
      mesaTickets
        .map((t) => t.mesa_type)
        .filter((v): v is "mesa" | "camarote" => v !== null),
    ),
  ]

  // Group mesa tickets by sector within each type
  const mesaBySector = (type: string) => {
    const ofType = mesaTickets.filter((t) => t.mesa_type === type)
    const sectorNames = [...new Set(ofType.map((t) => t.mesa_sector ?? ""))]
    return sectorNames.map((sectorName) => {
      const products = ofType.filter((t) => (t.mesa_sector ?? "") === sectorName)
      const mapImage = products[0]?.mesa_map_image ?? null
      return { name: sectorName, products, mapImage }
    })
  }

  const handleMesaSelect = (mesaType: string, sectorName: string, productId: string) => {
    const key = `${mesaType}:${sectorName}`

    setSelectedMesas((prev) => {
      if (!productId) {
        // Deselecting: drop the key instead of storing "" so the map
        // doesn't silently accumulate empty-string entries over a session.
        const rest = { ...prev }
        delete rest[key]
        return rest
      }
      return { ...prev, [key]: productId }
    })

    if (!productId) {
      // Clear selection: set qnt=0 for all products in this sector
      setTickets((prev) =>
        prev.map((t) =>
          t.mesa_type === mesaType && (t.mesa_sector ?? "") === sectorName
            ? { ...t, qnt: 0 }
            : t,
        ),
      )
      return
    }

    // Set selected product qnt=1, clear others in same sector
    setTickets((prev) =>
      prev.map((t) => {
        if (t.mesa_type !== mesaType || (t.mesa_sector ?? "") !== sectorName) return t
        return { ...t, qnt: t.id === productId && (t.quantity ?? 0) > 0 ? 1 : 0 }
      }),
    )
  }

  const handleSectorToggle = (type: string, sectorName: string) => {
    setExpandedSector((prev) => ({
      ...prev,
      [type]: prev[type] === sectorName ? null : sectorName,
    }))
  }

  return (
    <S.Component $inline={inline}>
      <S.Top>
        <span>Ingressos</span>
      </S.Top>

      {/* {regularTickets.length > 0 && (
        <S.SectionLabel>Ingressos avulsos</S.SectionLabel>
      )} */}

      <S.Tickets>
        {!Boolean(event?.keep_sells_online) ||
        (event && !checkEndingDate(event as any))
          ? renderKeepOutSellsMessage()
          : showByGroup
            ? groups.map((g, gKey) => (
                <TicketsGroup
                  group_name={g.name}
                  key={gKey}
                  k={6}
                  data={regularTickets.filter((i) => i.group_id === g.id)}
                  changeQnt={changeQnt}
                />
              ))
            : regularTickets.map((t, k) => (
                <Ticket k={k} key={k} ticket={t} changeQnt={changeQnt} />
              ))}
      </S.Tickets>

      {/* Mesa/Camarote UI */}
      {mesaTickets.length > 0 && (
        <>
          {/* <S.SectionLabel>Mesas e Camarotes</S.SectionLabel> */}
          <div>
            {mesaTypes.length > 1 && (
              <S.MesaTabs>
                {mesaTypes.map((type) => (
                  <S.MesaTab
                    key={type}
                    $active={activeTab === type}
                    onClick={() => setActiveTab(type)}
                  >
                    {type === "mesa" ? "Mesa" : "Camarote"}
                  </S.MesaTab>
                ))}
              </S.MesaTabs>
            )}

            {/* Active tab content: sectors */}
            {activeTab &&
              mesaBySector(activeTab).map((sector) => (
                <MesaSectorGroup
                  key={`${activeTab}:${sector.name}`}
                  sectorName={sector.name}
                  mesaType={activeTab as "mesa" | "camarote"}
                  products={sector.products}
                  mapImage={sector.mapImage}
                  selectedId={selectedMesas[`${activeTab}:${sector.name}`] ?? null}
                  expanded={expandedSector[activeTab] === sector.name}
                  onToggle={() => handleSectorToggle(activeTab!, sector.name)}
                  onSelect={(productId) =>
                    handleMesaSelect(activeTab!, sector.name, productId ?? "")
                  }
                  onViewMap={(imageUrl) => setMapModal({ open: true, imageUrl })}
                />
              ))}
          </div>
        </>
      )}

      {tickets.some((t) => t.qnt > 0) && (
        <S.Summary>
          {tickets
            .filter((t) => t.qnt > 0)
            .map((t) => (
              <S.SummaryLine key={t.id} data-testid="summary-line">
                {t.qnt}x {t.name} — {formatMoney(t.price_sell * t.qnt, true)}
              </S.SummaryLine>
            ))}
        </S.Summary>
      )}

      {Boolean(event?.keep_sells_online) &&
      checkEndingDate(event as any) &&
      tickets.length > 0 ? (
        <S.Bottom>
          <S.TaxesResume>
            <S.TaxResume>
              <span>Subtotal</span>
              <span>{formatMoney(ticketsTotal, true)}</span>
            </S.TaxResume>
            {event?.eCommerce?.chargeClient && eventHasTaxes(event) && (
              <S.TaxResume>
                <span>Taxas {taxes.strComplement}</span>
                <span>
                  {formatMoney(ticketsTotal > 0 ? taxes.value : 0, true)}
                </span>
              </S.TaxResume>
            )}
          </S.TaxesResume>
          <S.BottomFinal>
            <S.Resume>
              <S.Total>{`Total ${formatMoney(total, true)}`}</S.Total>
            </S.Resume>
            <S.BuyBtn
              onClick={
                tickets.every((t) => t.qnt === 0) ? undefined : handleBuy
              }
              disabled={tickets.every((t) => t.qnt === 0)}
            >
              Comprar
            </S.BuyBtn>
          </S.BottomFinal>
        </S.Bottom>
      ) : (
        <S.KeepOutSellsOnlineMessage>
          Sem ingressos ativos no momento.
        </S.KeepOutSellsOnlineMessage>
      )}

      {/* Map modal */}
      {mapModal.open && ReactDOM.createPortal(
        <div
          onClick={() => setMapModal({ open: false, imageUrl: "" })}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={mapModal.imageUrl}
            alt="Mapa do setor"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }}
          />
        </div>,
        document.body,
      )}
    </S.Component>
  )
}

export default memo(TicketsControl)
